import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import EditorialMarquee from "@/components/EditorialMarquee";
import { FadeUp, MaskedLine, SectionLabel } from "@/components/Reveal";
import { useContent } from "@/lib/api";

const PRINCIPLES = [
  ["Personalizzazione", "Nessuna scheda uguale all'altra: il programma nasce da te, dal tuo corpo e dalla tua vita."],
  ["Tecnica", "Prima il movimento corretto, poi il carico. La tecnica è la base di ogni progresso."],
  ["Programmazione", "Ogni settimana ha un senso dentro un percorso più grande, pianificato e misurabile."],
  ["Progressione", "Carichi, volumi e difficoltà crescono con te, passo dopo passo, senza salti."],
  ["Costanza", "Non serve una routine perfetta: serve un percorso che riesci a sostenere davvero."],
  ["Flessibilità", "Turni, imprevisti, settimane storte: il programma si adatta, non si rompe."],
  ["Monitoraggio", "I progressi si misurano, si osservano e guidano ogni modifica al percorso."],
];

export default function Home() {
  const content = useContent();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <div data-testid="home-page">
      <Nav />
      <section ref={heroRef} className="relative flex min-h-screen items-center overflow-hidden pt-24" data-testid="hero-section">
        <div className="mx-auto grid w-full max-w-7xl gap-16 px-6 lg:px-12">
          <motion.div style={{ y: textY }} className="flex max-w-3xl flex-col justify-center py-16">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 1 }}
              className="label-caps mb-8 text-mist"
              data-testid="hero-label"
            >
              Training &amp; Coaching
            </motion.p>
            <h1 className="font-display text-5xl leading-[0.95] text-ivory sm:text-7xl lg:text-8xl" data-testid="hero-title">
              <MaskedLine delay={0.3}>MARTINA</MaskedLine>
              <MaskedLine delay={0.45} className="italic text-mist">SERRA</MaskedLine>
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 max-w-md font-display text-xl italic text-ivory/90 md:text-2xl"
              data-testid="hero-subtitle"
            >
              {content.hero_subtitle || "Un allenamento costruito intorno a te, ai tuoi obiettivi e alla tua vita."}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-6 max-w-md text-sm leading-relaxed text-mute md:text-base"
              data-testid="hero-text"
            >
              {content.hero_text}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <Link to="/coaching" data-testid="hero-cta-coaching" className="btn-primary">
                Scopri il coaching <ArrowRight size={14} />
              </Link>
              <Link to="/pacchetti" data-testid="hero-cta-packages" className="btn-outline">
                Vedi i pacchetti
              </Link>
            </motion.div>
          </motion.div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
          className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 text-mute lg:block"
        >
          <ArrowDown size={18} className="animate-bounce" />
        </motion.div>
      </section>

      <EditorialMarquee />

      <section className="mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-40" data-testid="approach-section">
        <div className="grid gap-16 lg:grid-cols-[1fr_1.4fr]">
          <div>
            <SectionLabel>Il mio approccio</SectionLabel>
            <FadeUp as="h2" delay={0.1} className="mt-6 font-display text-4xl leading-tight text-ivory md:text-5xl">
              Non serve una routine perfetta.
              <span className="italic text-mist"> Serve un percorso sostenibile.</span>
            </FadeUp>
            <FadeUp delay={0.2} className="mt-8 max-w-md text-sm leading-relaxed text-mute md:text-base">
              Il mio metodo si basa su sette principi semplici. Ogni percorso è costruito per adattarsi
              alla persona, non il contrario: perché i risultati arrivano quando l'allenamento entra
              nella tua vita, senza stravolgerla.
            </FadeUp>
          </div>
          <div className="flex flex-col justify-center">
            {PRINCIPLES.map(([title, desc], i) => (
              <FadeUp key={title} delay={i * 0.06} className="group flex gap-8 border-b border-line py-8 first:border-t" data-testid={`principle-${i + 1}`}>
                <span className="font-display text-4xl text-sage transition-colors duration-500 group-hover:text-mist md:text-5xl">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div>
                  <h3 className="font-display text-2xl text-ivory md:text-3xl">{title}</h3>
                  <p className="mt-2 max-w-md text-sm leading-relaxed text-mute">{desc}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-line bg-surface" data-testid="home-coaching-teaser">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-28 lg:grid-cols-2 lg:px-12 lg:py-40">
          <div>
            <SectionLabel>Coaching online</SectionLabel>
            <FadeUp as="h2" delay={0.1} className="mt-6 font-display text-4xl leading-tight text-ivory md:text-6xl">
              Il tuo percorso,
              <br />
              <span className="italic text-mist">costruito su di te.</span>
            </FadeUp>
          </div>
          <div className="flex flex-col justify-end">
            <FadeUp delay={0.2} className="text-sm leading-relaxed text-mute md:text-base">
              Valutazione iniziale, programmazione personalizzata, monitoraggio dei progressi e analisi
              della tecnica tramite video. Un accompagnamento continuo, ovunque tu sia.
            </FadeUp>
            <FadeUp delay={0.3} className="mt-10 flex flex-wrap gap-4">
              <Link to="/coaching" data-testid="teaser-cta-coaching" className="btn-primary">
                Come funziona <ArrowRight size={14} />
              </Link>
              <Link to="/pacchetti" data-testid="teaser-cta-packages" className="btn-outline">
                Vedi i pacchetti
              </Link>
            </FadeUp>
          </div>
        </div>
      </section>

      <Footer content={content} />
    </div>
  );
}
